import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  notification,
  Row,
  Col,
  InputNumber,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { CreateCustomerRequest } from '../../model/Customer.model';
import customerService from '../../service/customer-service/Customer.service';
import partnerService from '../../service/partner-service/Partner.service';
import { Partner } from '../../model/Partner.model';
import dayjs from 'dayjs';
import useEmployee from '../../hook/useEmployee';

const { TextArea } = Input;

interface CreateCustomerModalProps {
  onSuccess: () => void;
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
}

const CreateCustomerModal: React.FC<CreateCustomerModalProps> = ({
  onSuccess,
  openModal,
  setOpenModal,
}) => {
  const [form] = Form.useForm();
  const [isProcessing, setIsProcessing] = useState(false);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(
    null
  );
  const { employee } = useEmployee(); // Lấy thông tin nhân viên đang đăng nhập

  // Reset form khi đóng modal
  useEffect(() => {
    if (!openModal) {
      form.resetFields();
      setSelectedPartnerId(null);
    }
  }, [openModal, form]);

  // Khởi tạo giá trị mặc định cho ngày đăng ký
  useEffect(() => {
    if (openModal) {
      form.setFieldValue('registrationDate', dayjs());
      fetchPartners();
    }
  }, [openModal, form]);

  const fetchPartners = async () => {
    try {
      setLoadingPartners(true);
      const data = await partnerService.getAllPartners();
      setPartners(data);
    } catch (error) {
      console.error('Lỗi khi lấy danh sách đối tác:', error);
    } finally {
      setLoadingPartners(false);
    }
  };

  const handlePartnerChange = (value: number | null) => {
    setSelectedPartnerId(value);
    if (!value) {
      form.setFieldValue('commissionRateForPartner', 0);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsProcessing(true);
      const values = await form.validateFields();

      if (!employee?.id) {
        notification.warning({
          message: 'Thông tin nhân viên không có sẵn',
          description:
            'Hệ thống sẽ tạo khách hàng mà không ghi nhận thông tin nhân viên tạo.',
        });
      }

      const formData: CreateCustomerRequest = {
        name: values.name,
        contactInfo: values.contactInfo,
        email: values.email,
        identificationNumber: values.identificationNumber,
        registrationDate: values.registrationDate?.format('YYYY-MM-DD') || '',
        note: values.note || '',
        partnerId: values.partnerId,
        commissionRateForPartner: values.partnerId
          ? values.commissionRateForPartner || 0
          : 0,
        employeeId: employee?.id,
      };

      await customerService.createCustomer(formData);
      notification.success({
        message: 'Tạo khách hàng thành công',
      });
      setOpenModal(false);
      onSuccess();
    } catch (error: any) {
      console.error('Error creating customer:', error);
      notification.error({
        message: 'Lỗi khi tạo khách hàng',
        description: error.response?.data || 'Vui lòng thử lại.',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <Button
        type="default"
        icon={<PlusOutlined />}
        onClick={() => setOpenModal(true)}
        className="flex items-center"
      >
        Tạo khách hàng mới
      </Button>

      <Modal
        title="Tạo khách hàng mới"
        open={openModal}
        onCancel={() => setOpenModal(false)}
        footer={[
          <Button key="back" onClick={() => setOpenModal(false)}>
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isProcessing}
            onClick={handleSubmit}
          >
            Tạo mới
          </Button>,
        ]}
        width={700}
        centered
        className="responsive-modal"
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Tên khách hàng"
                rules={[
                  { required: true, message: 'Vui lòng nhập tên khách hàng' },
                ]}
              >
                <Input placeholder="Nhập tên khách hàng" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="contactInfo"
                label="Số điện thoại"
                rules={[
                  {
                    pattern: /^[0-9]+$/,
                    message: 'Vui lòng nhập số điện thoại hợp lệ',
                  },
                ]}
              >
                <Input placeholder="Nhập số điện thoại" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  {
                    type: 'email',
                    message: 'Vui lòng nhập đúng định dạng email',
                  },
                ]}
              >
                <Input placeholder="Nhập địa chỉ email" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="identificationNumber" label="Số CCCD/CMT">
                <Input placeholder="Nhập số CCCD/CMT" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="partnerId" label="Đối tác">
                <Select
                  allowClear
                  showSearch
                  placeholder="Chọn đối tác"
                  loading={loadingPartners}
                  optionFilterProp="children"
                  onChange={handlePartnerChange}
                  filterOption={(input, option) =>
                    (option?.label?.toString().toLowerCase() || '').includes(
                      input.toLowerCase()
                    )
                  }
                  options={partners.map((partner) => ({
                    value: partner.id,
                    label: partner.name,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="registrationDate" label="Ngày đăng ký">
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="Chọn ngày đăng ký"
                />
              </Form.Item>
            </Col>

            {selectedPartnerId && (
              <Col xs={24} md={12}>
                <Form.Item
                  name="commissionRateForPartner"
                  label="Phần trăm hoa hồng (%)"
                  initialValue={0}
                  rules={[
                    {
                      type: 'number',
                      min: 0,
                      max: 100,
                      message: 'Phần trăm hoa hồng phải từ 0 đến 100%',
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.1}
                    style={{ width: '100%' }}
                    placeholder="Nhập phần trăm hoa hồng"
                  />
                </Form.Item>
              </Col>
            )}

            <Col span={24}>
              <Form.Item name="note" label="Ghi chú">
                <TextArea rows={3} placeholder="Nhập ghi chú" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default CreateCustomerModal;
